import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { agentsData } from "Stores/agentsData";
import AgentPageSkeleton from "./AgentPageSkeleton";
import { AgentOrb, whenLabel, runWhen, cleanSearchName } from "./AgentCard";
import AgentsCrumb from "./AgentsCrumb";
import AgentSelect from "./AgentSelect";
import AgentTimezoneField from "./AgentTimezoneField";
import DayOfWeekField, { FREQUENCY_OPTIONS, daysOf, scheduleText } from "./DayOfWeekField";
import { icpToListBuilderImport, encodeListBuilderImport } from "./icpListBuilder";
import { dataSearchState } from "../dataSearchV2/state";
import TriggerFilters from "./TriggerFilters";
import { eventOptions, filterConfigFor, filterSummary } from "./triggerCatalog";


// IMPORTANT_ONLY is the old stored name for ON_FAILURE — an agent saved before the rename still has it.
const NOTIFY_LABEL = {
  DAILY_DIGEST: "Daily digest", EVERY_RUN: "After every run",
  ON_FAILURE: "Only when a run fails", IMPORTANT_ONLY: "Only when a run fails", OFF: "Off"
};
const NOTIFY_OPTIONS = [
  { value: "DAILY_DIGEST", label: "Daily digest" },
  { value: "EVERY_RUN", label: "After every run" },
  { value: "ON_FAILURE", label: "Only when a run fails" },
  { value: "OFF", label: "Off" }
];
const CHANNEL_LABEL = { email: "Email", slack: "Slack" };

const EVENT_SHORT = {
  EMAIL_REPLIED: "Email replied", LINKEDIN_REPLIED: "LinkedIn replied",
  LI_CONNECTION_REQUEST_ACCEPTED: "LinkedIn connection accepted", EMAIL_OPENED: "Email opened",
  EMAIL_CLICKED: "Link clicked", WEBSITE_VISITED: "Website visited", CONTACT_CREATED: "Contact created",
  CONTACT_UPDATED: "Contact updated", CONTACT_STAGE_CHANGED: "Stage changed",
  ADDED_TO_SEQUENCE: "Added to sequence", SEQUENCE_COMPLETED: "Sequence finished",
  COMPLETED_ON_ALL_SEQUENCES: "Finished sequences", CALL_LOGGED: "Call logged",
  CONTACT_OPTEDOUT: "Opted out", EMAIL_BOUNCED: "Email bounced", EMAIL_DELIVERED: "Email delivered"
};
const STATUS_LABEL = {
  SUCCEEDED: "Succeeded", FAILED: "Failed", PARTIAL: "Partial", CANCELLED: "Cancelled",
  RUNNING: "Running", QUEUED: "Queued", WAITING_APPROVAL: "Awaiting approval", SLEEPING: "Waiting"
};
const STEP_KIND_LABEL = {
  SOURCE: "Source", EXPAND: "Expand", ENRICH: "Enrich", FILTER: "Filter", BRANCH: "Branch",
  WAIT: "Wait", ACT: "Action", APPROVE: "Approval", NOTIFY: "Notify"
};
// Agent lifecycle status → header pill label.
const AGENT_STATUS_LABEL = { ACTIVE: "Live", PAUSED: "Paused", DRAFT: "Draft", ARCHIVED: "Archived", DELETED: "Deleted" };
const prettify = s => String(s || "").replace(/_/g, " ").toLowerCase().replace(/^./, c => c.toUpperCase());
const friendlyTrigger = src => {
  if (!src) return "—";
  if (src.startsWith("event.")) { const k = src.slice(6); return EVENT_SHORT[k] || prettify(k); }
  if (src === "schedule.recurring") return "Scheduled";
  if (src === "manual.test") return "Test run";
  if (src === "manual") return "Manual";
  return prettify(src);
};
const runPill = st => st === "SUCCEEDED" ? "ag-pill--live" : (st === "FAILED" || st === "CANCELLED") ? "ag-pill--warn" : "ag-pill--draft";

const triggerKindOf = agent => {
  const t = agent.trigger && agent.trigger.type;
  if (t === "schedule.recurring") return "schedule";
  if (t && t.startsWith("event.")) return "event";
  return "manual";
};

const initSettings = agent => {
  const tc = (agent.trigger && agent.trigger.config) || {};
  const cfg = agent.config || {};
  return {
    name: agent.name || "",
    frequency: tc.frequency || "DAILY",
    time: tc.time || "08:00",
    days: daysOf(tc),
    eventType: agent.trigger && agent.trigger.type && agent.trigger.type.startsWith("event.") ? agent.trigger.type.slice(6) : "EMAIL_REPLIED",
    eventConfig: tc,
    dailyCap: cfg.dailyRecordCap != null ? cfg.dailyRecordCap : 25,
    notifyMode: cfg.notifyMode === "IMPORTANT_ONLY" ? "ON_FAILURE" : (cfg.notifyMode || "DAILY_DIGEST"),
    // Nothing saved means email, which is what every agent did before Slack was an option.
    notifyChannels: (cfg.notifyChannels && cfg.notifyChannels.length) ? cfg.notifyChannels.slice() : ["email"],
    slackChannelId: cfg.slackChannelId || "",
    timezone: cfg.timezone || "UTC",
    requireApproval: cfg.approvalMode === "REQUIRED"
  };
};

// "Daily digest · Email, Slack #pipeline" for the overview strip.
const notifySummary = (edit, agent) => {
  const mode = edit && edit.notifyMode;
  if (!mode) return "\u2014";
  const label = NOTIFY_LABEL[mode] || "\u2014";
  if (mode === "OFF") return label;
  const channels = (edit.notifyChannels || []).map(ch => {
    if (ch !== "slack") return CHANNEL_LABEL[ch] || ch;
    const cfg = (agent && agent.config) || {};
    return cfg.slackChannelName ? "Slack #" + cfg.slackChannelName : "Slack";
  });
  return channels.length ? label + " \u00b7 " + channels.join(", ") : label;
};

// Friendly, human-readable trigger summary for the overview strip.
const triggerSummary = agent => {
  const t = agent.trigger || {};
  if (t.type === "schedule.recurring" && t.config) {
    // An agent saved without a zone runs on UTC — say so rather than implying local time.
    const s = scheduleText(t.config.frequency, t.config.time, daysOf(t.config), (agent.config && agent.config.timezone) || "UTC");
    return s.charAt(0).toUpperCase() + s.slice(1);
  }
  if (t.type && t.type.startsWith("event.")) {
    const narrowed = filterSummary(t.type.slice("event.".length), t.config);
    return friendlyTrigger(t.type) + (narrowed ? ` · ${narrowed}` : "");
  }
  return "Manual";
};

const DISPOSITION = {
  ENROLLED: { label: "Enrolled", cls: "ag-disp--good" },
  PROCESSED: { label: "Processed", cls: "ag-disp--good" },
  WOULD_ENROLL: { label: "Would enroll", cls: "ag-disp--neutral" },
  WOULD_PROCESS: { label: "Would process", cls: "ag-disp--neutral" },
  IN_PROGRESS: { label: "In progress", cls: "ag-disp--wait" },
  STOPPED: { label: "Stopped", cls: "ag-disp--skip" },
  SOURCED: { label: "Sourced", cls: "ag-disp--neutral" },
  AWAITING_APPROVAL: { label: "Awaiting approval", cls: "ag-disp--wait" },
  WAITING: { label: "Waiting", cls: "ag-disp--wait" },
  REJECTED: { label: "Not approved", cls: "ag-disp--skip" },
  SKIPPED: { label: "Skipped", cls: "ag-disp--skip" }
};

const AgentDetail = observer(() => {
  const { id } = useParams();
  const history = useHistory();
  const [filters, setFilters] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [runsLoaded, setRunsLoaded] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [edit, setEdit] = useState(null);
  const [savedTick, setSavedTick] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [stepDefs, setStepDefs] = useState(agentsData.stepTypesCache || []);
  const [askRun, setAskRun] = useState(null); // { fields:[{key,label,type,options}], values:{} }
  // Inline rename of the title. Every agent starts with its template's default name; this is how
  // the user makes it theirs. ?rename=1 (the card menu's "Rename") opens it straight away.
  const params = new URLSearchParams(useLocation().search);
  const wantsRename = params.get("rename") === "1";
  // ?setup=1 ("Finish setup" on a card) lands on the ICP panel, the step that unblocks a draft.
  const wantsSetup = params.get("setup") === "1";
  const icpRef = useRef(null);
  const [renaming, setRenaming] = useState(wantsRename);
  const [nameDraft, setNameDraft] = useState("");
  const renameCancelled = useRef(false); // Escape: the blur that follows must not save
  const [starting, setStarting] = useState(false);


  useEffect(() => {
    setNotFound(false);
    setRunsLoaded(false);
    // Render straight away from the agent already in memory (the list, or the create call);
    // only a cold open (deep link / refresh) waits for the fetch.
    const seeded = agentsData.seedAgent(id);
    if (seeded) setEdit(initSettings(seeded));
    setLoaded(!!seeded);
    agentsData.fetchTriggerTypes(); // event labels + which filters each event offers
    agentsData.fetchSequencesLite(); // so a sequence filter shows its name, not its id
    agentsData.fetchSlackChannels(); // empty list = Slack isn't connected, so we can say so
    if (!agentsData.overview) agentsData.fetchOverview(); // next run time is worked out there
    agentsData.fetchStepTypes().then(setStepDefs); // step names, in parallel with the agent
    (async () => {
      const agent = await agentsData.getAgent(id);
      if (!agent) {
        setNotFound(true);
        setLoaded(true);
        return;
      }
      setEdit(initSettings(agent));
      setLoaded(true);
      // Runs and the ICP preview fill in behind the page instead of holding it.
      agentsData.fetchRuns(id).then(() => setRunsLoaded(true));
      const icp = agent.templateKey === "daily_icp_prospector" || (agent.config && agent.config.sellerProfileId);
      if (icp) {
        agentsData.fetchSavedSearches(); // the ICP is one of the member's List Builder saved searches
        agentsData.previewIcp(id).then(preview => setFilters((preview && preview.filters) || {}));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // A run in flight moves on its own here too (the card and Watch live poll the same way).
  const hasActive = (agentsData.runs || []).some(r => r.status === "RUNNING" || r.status === "QUEUED");
  useEffect(() => {
    if (!hasActive) return undefined;
    const t = setInterval(() => { if (!document.hidden) agentsData.fetchRuns(id); }, 5000);
    return () => clearInterval(t);
  }, [hasActive, id]);

  useEffect(() => {
    if (loaded && wantsSetup && icpRef.current) icpRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [loaded, wantsSetup]);

  const agent = agentsData.currentAgent;
  const { runs, busy, testing } = agentsData;
  if (notFound) {
    return (
      <div className="ag-page">
        <div className="ag-empty">
          <div className="ag-empty__title">Agent not found</div>
          <div className="ag-empty__actions">
            <button className="ag-btn ag-btn--primary" onClick={() => history.push("/agents")}><i className="material-icons-outlined">arrow_back</i>Back to Agents</button>
          </div>
        </div>
      </div>
    );
  }
  if (!loaded || !agent) return <AgentPageSkeleton />;

  const isIcp = agent.templateKey === "daily_icp_prospector" || (agent.config && agent.config.sellerProfileId);
  // Runs of agents that don't enroll (export, tasks, tags…) finish contacts as "Processed", not "Enrolled".
  const enrolls = (agent.flow || []).some(n => n.stepType === "act.enroll_sequence");
  const doneLabel = enrolls ? "Enrolled" : "Processed";
  const approved = isIcp ? (agent.config && agent.config.icpApproved) : true;
  const kind = triggerKindOf(agent);

  // Friendly step name from the registry (falls back to the raw type).
  const stepName = type => {
    const d = (stepDefs || []).find(x => x.type === type);
    return (d && d.displayName) || String(type || "").split(".").pop().replace(/_/g, " ");
  };
  // The step's kind label ("Source", "Action"…); before step defs load, read it off the type prefix.
  const stepKindLabel = type => {
    const d = (stepDefs || []).find(x => x.type === type);
    const kind = d && d.kind ? d.kind : String(type || "").split(".")[0].toUpperCase();
    return STEP_KIND_LABEL[kind] || prettify(kind);
  };

  // A step's own settings in a line ("Sequence: Q4 founders · Up to 50 per run"), so the page says
  // what the agent will do without opening the editor. Ids resolve to names; unknown ids are left out.
  const stepDetail = n => {
    const def = (stepDefs || []).find(d => d.type === n.stepType);
    const vals = n.params || {};
    const parts = [];
    if (n.stepType === "source.find_people_icp") {
      parts.push(approved ? `ICP: ${icpSearch ? cleanSearchName(icpSearch.label) : "saved search"}` : "Needs your ICP");
    }
    ((def && def.params) || []).forEach(p => {
      const v = vals[p.key];
      if (v === undefined || v === null || v === "" || v === false || (Array.isArray(v) && !v.length)) return;
      if (v === "{{ask}}") { parts.push(`${p.label}: asked each run`); return; }
      if (v === true) { parts.push(p.label); return; }
      const opt = (p.options || []).find(o => String(o.value) === String(v));
      const seq = (agentsData.sequencesLite || []).find(x => x.value === v);
      const ss = (agentsData.savedSearches || []).find(x => x.id === v);
      const val = opt ? opt.label : seq ? seq.label : ss ? ss.label
        : Array.isArray(v) ? `${v.length} selected`
        : typeof v === "object" || /^[0-9a-f]{24}$/i.test(String(v)) ? null : String(v);
      if (val) parts.push(`${p.label}: ${val}`);
    });
    const cap = agent.config && agent.config.dailyRecordCap;
    if (cap && String(n.stepType).startsWith("source.")) parts.push(`Up to ${cap} per run`);
    return parts.slice(0, 3).join(" · ");
  };
  // The one-liner quotes names ("Q4 founders"); show them as names, not as a log line.
  const summaryNodes = text => String(text).replace(/\bmy ICP\b/g, "your ICP").split(/"([^"]+)"/).map((part, i) => (i % 2 ? <b key={i}>{part}</b> : part));

  // Test run → open it, where the plan and every contact it would add are shown.
  const onTestRun = async () => {
    const run = await agentsData.testRun(id);
    if (run && run.id) history.push(`/agents/${id}/runs/${run.id}`);
    else agentsData.fetchRuns(id);
  };

  // "Ask each run" inputs: params stored as "{{ask}}" on the flow, resolved to labels via step defs.
  const askFields = () => {
    const byType = {};
    (stepDefs || []).forEach(d => { byType[d.type] = d; });
    const seen = {};
    const out = [];
    (agent.flow || []).forEach(n => {
      const params = (byType[n.stepType] && byType[n.stepType].params) || [];
      params.forEach(p => {
        if (n.params && n.params[p.key] === "{{ask}}" && !seen[p.key]) {
          seen[p.key] = true;
          out.push({ key: p.key, label: p.label, type: p.type, options: p.options });
        }
      });
      // A post import asked for a new URL each run also asks who to import for that post —
      // reactions, comments, or both — starting from the agent's saved choice.
      if (n.stepType === "source.from_linkedin_post" && n.params && n.params.url === "{{ask}}" && !seen.engagementType) {
        const p = params.find(x => x.key === "engagementType");
        if (p) {
          seen.engagementType = true;
          const saved = n.params.engagementType && n.params.engagementType !== "{{ask}}" ? n.params.engagementType : "all";
          out.push({ key: p.key, label: p.label, type: p.type, options: p.options, defaultValue: saved });
        }
      }
    });
    return out;
  };
  // Start the run and open it in Watch live, rather than waiting on this page.
  const startAndWatch = async inputs => {
    setStarting(true);
    const run = await agentsData.startRun(id, inputs);
    setStarting(false);
    if (run && run.id) history.push(`/agents/${id}/runs/${run.id}`);
  };
  const onRunNow = () => {
    const fields = askFields();
    if (!fields.length) { startAndWatch(); return; }
    const values = {};
    fields.forEach(f => { values[f.key] = f.defaultValue || ""; });
    setAskRun({ fields, values });
  };
  const askRunReady = askRun && askRun.fields.every(f => String(askRun.values[f.key] || "").trim());
  const submitAskRun = () => {
    const vals = { ...askRun.values };
    setAskRun(null);
    startAndWatch(vals);
  };
  const setE = (k, v) => setEdit({ ...edit, [k]: v });
  const lastReal = (runs || []).find(r => !r.dryRun);
  const everyRun = (agent.config && agent.config.approvalMode) === "REQUIRED";
  const card = ((agentsData.overview && agentsData.overview.agents) || []).find(c => c.agent && c.agent.id === id);
  const nextRunAt = card && card.nextRunAt;
  const activeRun = (runs || []).find(r => r.status === "RUNNING" || r.status === "QUEUED");
  const startRename = () => { renameCancelled.current = false; setNameDraft(agent.name || ""); setRenaming(true); };
  const saveName = async () => {
    const n = nameDraft.trim();
    setRenaming(false);
    if (renameCancelled.current) return;
    if (!n || n === agent.name) return;
    const saved = await agentsData.updateAgent(id, { name: n });
    if (saved) { setEdit(e => (e ? { ...e, name: saved.name } : e)); }
  };

  // Open List Builder pre-filled from the seller profile's ICP; its "Save as ICP" links the search back here.
  const buildIcpSearch = async () => {
    // Departments resolve against List Builder's Job Function catalog (cached after the first load).
    await dataSearchState.loadDepartmentsCatalog();
    const catalog = { departments: dataSearchState.departmentsCatalog, legacyAliases: dataSearchState.departmentLegacyAliases };
    const { filters: lbFilters, selected } = icpToListBuilderImport(filters, catalog);
    const payload = { filters: lbFilters, selected, icpAgent: { id, name: agent.name } };
    history.push(`/list-builder?sgLb=${encodeListBuilderImport(payload)}`);
  };
  const icpSearchId = agent && agent.config && agent.config.icpSavedSearchId;
  const icpSearch = icpSearchId && (agentsData.savedSearches || []).find(s => s.id === icpSearchId);
  const setStatus = async status => { await agentsData.setStatus(id, status); };
  const needsIcpText = "Set who it should look for first";

  // Draft setup: ICP (for ICP agents) → a test run → switch on. The first unfinished item is the one to do.
  // Any run counts as tested (a restored agent may only have real runs, or a test older than the last 20).
  const tested = (runs || []).length > 0;
  const setup = [
    isIcp && {
      key: "icp", done: !!approved, title: "Tell it who to look for",
      hint: approved ? `Using ${icpSearch ? cleanSearchName(icpSearch.label) : "your saved search"}.` : "Build your ICP in List Builder, or pick a saved search you already have.",
      action: null // the ICP panel sits right under the checklist while this is open
    },
    {
      key: "test", done: tested, title: "Do a test run",
      hint: "See who it would add. Nothing is sent and nobody is enrolled.",
      action: <button className="ag-btn ag-btn--primary ag-btn--sm" disabled={busy || testing} onClick={onTestRun}><i className="material-icons-outlined">visibility</i>{testing ? "Testing…" : "Test run"}</button>
    },
    {
      key: "on", done: false, title: "Switch it on",
      hint: `It runs ${triggerSummary(agent).replace(/^./, c => c.toLowerCase())}. Its first run waits for your approval before anyone is enrolled.`,
      action: <button className="ag-btn ag-btn--primary ag-btn--sm" onClick={() => setStatus("ACTIVE")}><i className="material-icons-outlined">power_settings_new</i>Turn on</button>
    }
  ].filter(Boolean);
  const currentStep = setup.find(x => !x.done);
  const saveSettings = async () => {
    if (!edit) return;
    // The name is only for display. When the channel list didn't load (Slack disconnected, or a
    // slow fetch) we can't resolve it — keep the stored one rather than wiping a good name.
    const slackChannel = (agentsData.slackChannels || []).find(c => c.id === edit.slackChannelId);
    const priorName = (agent.config || {}).slackChannelName;
    const cfg = {
      ...(agent.config || {}), notifyMode: edit.notifyMode,
      notifyChannels: edit.notifyMode === "OFF" ? [] : edit.notifyChannels,
      slackChannelId: edit.slackChannelId || null,
      slackChannelName: !edit.slackChannelId ? null : (slackChannel ? slackChannel.name : (priorName || null)),
      approvalMode: edit.requireApproval ? "REQUIRED" : "NONE", dailyRecordCap: Number(edit.dailyCap),
      timezone: edit.timezone || "UTC"
    };
    let trigger = agent.trigger;
    if (kind === "schedule") trigger = { type: "schedule.recurring", config: { ...((agent.trigger && agent.trigger.config) || {}), frequency: edit.frequency, time: edit.time, days: edit.frequency === "WEEKLY" ? edit.days : undefined, dayOfWeek: undefined } };
    else if (kind === "event") trigger = { type: "event." + edit.eventType, config: filterConfigFor(edit.eventType, edit.eventConfig) };
    // "Require approval" is enforced by the engine from config.approvalMode (it pauses before the first
    // outbound step every run). Drop any legacy approval.gate step — that step type no longer exists,
    // so a flow containing it would fail to run.
    const flow = (agent.flow || []).map(n => ({ ...n })).filter(n => n.stepType !== "approval.gate");
    await agentsData.updateAgent(id, { name: edit.name, config: cfg, trigger, flow });
    setSavedTick(true); setTimeout(() => setSavedTick(false), 2000);
  };

  const icpPanel = (
    <div className="ag-panel" ref={icpRef}>
      <div className="ag-panel__head">
        <h2 className="ag-panel__title">Ideal customer profile</h2>
        {approved && <span className="ag-pill ag-pill--live">Approved</span>}
      </div>
      {icpSearchId ? (
        <p className="ag-hint">
          Every run finds new people with the List Builder saved search <b>{icpSearch ? cleanSearchName(icpSearch.label) : "(saved search)"}</b>.
        </p>
      ) : (
        <p className="ag-hint">
          Start from your seller profile in List Builder, refine the filters, check the results, then save it as this
          agent's ICP. The agent can't run until you do.
        </p>
      )}
      <div className="ag-icpbox">
        <button className="ag-btn ag-btn--primary" disabled={busy} onClick={buildIcpSearch}><i className="material-icons-outlined">search</i>
          {icpSearchId ? "Rebuild in List Builder" : "Build ICP in List Builder"}
        </button>
        <span className="ag-icpbox__or">or</span>
        <div className="ag-icpbox__pick">
          <AgentSelect
            searchable
            placeholder={icpSearchId ? "Use a different saved search…" : "Use a saved search you already have…"}
            value={icpSearchId || ""}
            onChange={v => v && v !== icpSearchId && agentsData.setIcpSavedSearch(id, v)}
            options={(agentsData.savedSearches || []).map(ss => ({ value: ss.id, label: ss.label }))}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="ag-detail">
      <div className="ag-shell__head">
        <div>
          <AgentsCrumb items={[{ label: agent.name }]} />
        </div>
        <div className="ag-actions">
          {/* A draft is switched on from its setup checklist, so the header only offers the runs. */}
          <button className="ag-btn" disabled={busy || testing || !approved} title={approved ? "" : needsIcpText} onClick={onTestRun}><i className="material-icons-outlined">visibility</i>{testing ? "Testing…" : "Test run"}</button>
          <button className="ag-btn" disabled={busy || testing || starting || !approved} title={approved ? "" : needsIcpText} onClick={onRunNow}><i className="material-icons-outlined">play_arrow</i>{starting ? "Starting…" : "Run now"}</button>
          {agent.status === "ACTIVE" ? (
            <button className="ag-btn" onClick={() => setStatus("PAUSED")}><i className="material-icons-outlined">pause</i>Pause</button>
          ) : (agent.status === "ARCHIVED" || agent.status === "DELETED") ? (
            <button className="ag-btn ag-btn--primary" onClick={() => setStatus("DRAFT")}><i className="material-icons-outlined">restore</i>Restore</button>
          ) : agent.status === "PAUSED" ? (
            <button className="ag-btn ag-btn--primary" onClick={() => setStatus("ACTIVE")}><i className="material-icons-outlined">play_arrow</i>Resume</button>
          ) : null}
        </div>
      </div>

      <div className="ag-detail__scroll">
        <div className="ag-detail__inner">

          <div className="ag-dhead">
            <AgentOrb mode={activeRun ? "working" : agent.status === "ACTIVE" ? "live" : agent.status === "DRAFT" ? "draft" : "rest"} />
            {renaming ? (
              <input className="ag-input ag-dhead__input" autoFocus value={nameDraft} maxLength={80}
                aria-label="Agent name"
                onFocus={e => { if (!nameDraft) setNameDraft(agent.name || ""); e.target.select(); }}
                onChange={e => setNameDraft(e.target.value)}
                onBlur={saveName}
                onKeyDown={e => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") { renameCancelled.current = true; setRenaming(false); } }} />
            ) : (
              <button className="ag-dhead__name" title="Rename" onClick={startRename}>
                {agent.name}<i className="material-icons-outlined">edit</i>
              </button>
            )}
          </div>

          {activeRun && (
            <div className="ag-livebar">
              <span className="ag-livedot" />
              <span>Running now · started {new Date(activeRun.startedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
              <button className="ag-btn ag-btn--primary ag-btn--sm" onClick={() => history.push(`/agents/${id}/runs/${activeRun.id}`)}><i className="material-icons-outlined">visibility</i>Watch live</button>
            </div>
          )}

          {/* What this agent is for, in a sentence — written from its own flow, so it stays true
              after an edit. Missing on an agent whose summary hasn't been written yet. */}
          {!!agent.summary && <p className="ag-detail__summary">{summaryNodes(agent.summary)}</p>}

          {agent.status === "DRAFT" && (
            <div className="ag-ready">
              <div className="ag-ready__head">
                <h2 className="ag-panel__title">Get it ready</h2>
                <span className="ag-ready__count">{setup.filter(x => x.done).length} of {setup.length} done</span>
              </div>
              <ol className="ag-ready__list">
                {setup.map((x, i) => (
                  <li key={x.key} className={`ag-ready__item${x.done ? " is-done" : ""}${x === currentStep ? " is-current" : ""}`}>
                    <span className="ag-ready__mark">{x.done ? <i className="material-icons-outlined">check</i> : i + 1}</span>
                    <span className="ag-ready__text"><b>{x.title}</b><span>{x.hint}</span></span>
                    {/* Turn on stays available once the ICP is set: the test run is advice, not a gate. */}
                    {(x === currentStep || (x.key === "on" && (!isIcp || approved))) && x.action}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {isIcp && !approved && icpPanel}

          {/* Overview strip */}
          <div className="ag-overview">
            <div className="ag-overview__facts">
              <div className="ag-ov">
                <span className="ag-ov__l">Status</span>
                <span className={`ag-pill ${agent.status === "ACTIVE" ? "ag-pill--live" : "ag-pill--draft"}`}>
                  {agent.status === "ACTIVE" && <span className="ag-dot" />}
                  {AGENT_STATUS_LABEL[agent.status] || "Draft"}
                </span>
              </div>
              <div className="ag-ov"><span className="ag-ov__l">Runs</span><span className="ag-ov__v">{triggerSummary(agent)}</span></div>
              {agent.status === "ACTIVE" && nextRunAt && <div className="ag-ov"><span className="ag-ov__l">Next run</span><span className="ag-ov__v">{whenLabel(nextRunAt).replace(/^./, c => c.toUpperCase())}</span></div>}
              <div className="ag-ov">
                <span className="ag-ov__l">Approval</span>
                <span className="ag-ov__v">{everyRun ? "You approve every run" : "First run only"}</span>
                <span className="ag-ov__hint">{everyRun ? "Each run waits for you before enrolling anyone" : "After that it enrolls on its own"}</span>
              </div>
              {!!(agent.config && agent.config.dailyRecordCap) && <div className="ag-ov"><span className="ag-ov__l">Per run</span><span className="ag-ov__v">Up to {agent.config.dailyRecordCap} contacts</span></div>}
              <div className="ag-ov"><span className="ag-ov__l">Notifications</span><span className="ag-ov__v">{notifySummary(edit, agent)}</span></div>
            </div>
            {/* Last real run only: a test run's numbers are a preview, shown on the run itself. */}
            {lastReal && (
              <div className="ag-overview__stats">
                <div className="ag-glance__stat"><div className="v v--good">{lastReal.enrolledCount}</div><div className="l">{doneLabel} last run</div></div>
                <div className="ag-glance__stat"><div className="v">{lastReal.skippedCount}</div><div className="l">Skipped</div></div>
                <div className="ag-glance__stat"><div className="v">{lastReal.creditsUsed}</div><div className="l">Credits</div></div>
              </div>
            )}
          </div>

          {/* A paused run can only continue from the Approvals queue — point there. */}

          {runs && runs.some(r => r.status === "WAITING_APPROVAL") && (

            <div className="ag-awaitbar">

              <i className="material-icons-outlined">hourglass_empty</i>

              <span>A run is paused, waiting for your review before the agent acts.</span>

              <button className="ag-btn ag-btn--primary ag-btn--sm" onClick={() => history.push("/agents/approvals")}><i className="material-icons-outlined">assignment_turned_in</i>Review</button>

            </div>

          )}

          {/* Recent runs, the primary content once it has run. A draft with none shows its checklist instead. */}
          {!(agent.status === "DRAFT" && (!runsLoaded || !(runs || []).length)) && (
          <div className="ag-panel ag-panel--flush">
            <div className="ag-panel__head">
              <h2 className="ag-panel__title">Recent runs</h2>
              <button className="ag-link" onClick={() => agentsData.fetchRuns(id)}><i className="material-icons-outlined">refresh</i>Refresh</button>
            </div>
            {!runsLoaded && <div className="ag-skel ag-skel--rows"><span className="linear-background" /><span className="linear-background" /><span className="linear-background" /></div>}
            {runsLoaded && (!runs || runs.length === 0) && <div className="ag-hint">No runs yet. Use “Run now” or “Test run” to see activity here.</div>}
            {runs && runs.length > 0 && (
              <table className="ag-table">
                <thead>
                  <tr><th>When</th><th>Started by</th><th>Status</th><th>{doneLabel}</th><th>Skipped</th><th>Credits</th><th /></tr>
                </thead>
                <tbody>
                  {runs.map(r => {
                    const active = r.status === "RUNNING" || r.status === "QUEUED";
                    // A test run changes nothing: its numbers are what it would do, so say so.
                    return (
                      <tr key={r.id} className={`ag-row--click${r.dryRun ? " ag-row--test" : ""}`}
                        onClick={() => history.push(`/agents/${id}/runs/${r.id}`)}>
                        <td>{runWhen(r.startedAt)}</td>
                        <td>
                          {r.dryRun ? <span className="ag-pill ag-pill--draft">Test run</span> : friendlyTrigger(r.triggerSource)}
                          {r.triggerNote && <span className="ag-run__why" title={r.triggerNote}>{r.triggerNote}</span>}
                        </td>
                        <td><span className={`ag-pill ${runPill(r.status)}`}>{STATUS_LABEL[r.status] || prettify(r.status)}</span></td>
                        <td>{r.dryRun ? <span className="ag-muted">{r.enrolledCount} would</span> : r.enrolledCount}</td>
                        <td>{r.dryRun ? <span className="ag-muted">{r.skippedCount} would</span> : r.skippedCount}</td>
                        <td>{r.dryRun ? <span className="ag-muted">about {r.creditsUsed}</span> : r.creditsUsed}</td>
                        <td><span className="ag-link">{active ? "Watch live" : "Open"}<i className="material-icons-outlined">arrow_forward</i></span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          )}

          {/* The agent's steps in order — so you can see what it does without opening the editor */}
          <div className="ag-panel">
            <div className="ag-panel__head">
              <h2 className="ag-panel__title">Steps</h2>
              <button className="ag-link" onClick={() => history.push(`/agents/${id}/edit`)}><i className="material-icons-outlined">edit</i>Edit flow</button>
            </div>
            {(agent.flow || []).filter(n => n.stepType !== "notify.digest").length === 0
              ? <div className="ag-hint">No steps yet.</div>
              : (
                <ol className="ag-flowlist">
                  {(agent.flow || []).filter(n => n.stepType !== "notify.digest").map((n, i) => (
                    <li key={n.nodeId || i} className="ag-flowlist__item">
                      <span className="ag-flowlist__kind">{stepKindLabel(n.stepType)}</span>
                      <span className="ag-flowlist__body">
                        <span className="ag-flowlist__name">{stepName(n.stepType)}</span>
                        {!!stepDetail(n) && <span className="ag-flowlist__detail">{stepDetail(n)}</span>}
                      </span>
                      {n.stepType === "source.find_people_icp" && !approved && <span className="ag-pill ag-pill--warn">Needs ICP</span>}
                    </li>
                  ))}
                </ol>
              )}
          </div>

          {isIcp && approved && icpPanel}

          {/* Settings — collapsible (editing is secondary) */}
          {edit && (
            <div className="ag-panel">
              <button className="ag-panel__toggle" onClick={() => setSettingsOpen(o => !o)}>
                <span className="ag-panel__title">Settings</span>
                <span className="ag-panel__toggleright">
                  {savedTick && <span className="ag-pill ag-pill--live"><span className="ag-dot" />Saved</span>}
                  <span className="ag-panel__chev">{settingsOpen ? "▾" : "▸"}</span>
                </span>
              </button>
              {settingsOpen && (
                <>
                  <div className="ag-icp" style={{ marginTop: 16 }}>
                    <label className="ag-field">
                      <span className="ag-field__label">Agent name</span>
                      <input className="ag-input" value={edit.name} onChange={e => setE("name", e.target.value)} />
                    </label>
                    {kind === "schedule" && (
                      <>
                        <label className="ag-field">
                          <span className="ag-field__label">Frequency</span>
                          <AgentSelect value={edit.frequency} onChange={v => setE("frequency", v)} options={FREQUENCY_OPTIONS} />
                        </label>
                        {edit.frequency === "WEEKLY" && <DayOfWeekField label="Days" value={edit.days} onChange={v => setE("days", v)} />}
                        <label className="ag-field">
                          <span className="ag-field__label">Run time</span>
                          <input className="ag-input" type="time" value={edit.time} onChange={e => setE("time", e.target.value)} />
                        </label>
                        {/* Not a <label>: it would forward clicks on the options to the picker's search box. */}
                        <div className="ag-field">
                          <span className="ag-field__label">Time zone</span>
                          <AgentTimezoneField value={edit.timezone} onChange={v => setE("timezone", v)} />
                        </div>
                      </>
                    )}
                    {kind === "event" && (
                      <>
                        <label className="ag-field">
                          <span className="ag-field__label">Trigger event</span>
                          <AgentSelect value={edit.eventType} searchable options={eventOptions()}
                            onChange={v => { setE("eventType", v); setE("eventConfig", {}); }} />
                        </label>
                        <TriggerFilters eventType={edit.eventType} config={edit.eventConfig}
                          onChange={v => setE("eventConfig", v)} />
                      </>
                    )}
                    {isIcp && (
                      <label className="ag-field">
                        <span className="ag-field__label">Contacts / run</span>
                        <input className="ag-input" type="number" min="1" value={edit.dailyCap} onChange={e => setE("dailyCap", e.target.value)} />
                      </label>
                    )}
                    <label className="ag-field">
                      <span className="ag-field__label">Notify me</span>
                      <AgentSelect value={edit.notifyMode} onChange={v => setE("notifyMode", v)} options={NOTIFY_OPTIONS} />
                    </label>
                    {edit.notifyMode !== "OFF" && (
                      <label className="ag-field">
                        <span className="ag-field__label">Send it to</span>
                        <div className="ag-chanpick">
                          {["email", "slack"].map(ch => {
                            const on = edit.notifyChannels.indexOf(ch) !== -1;
                            const noSlack = ch === "slack" && agentsData.slackChannelsLoaded
                              && !(agentsData.slackChannels || []).length;
                            return (
                              <label key={ch} className={"ag-chanpick__opt" + (noSlack ? " ag-chanpick__opt--off" : "")}>
                                <input type="checkbox" checked={on} disabled={noSlack}
                                  onChange={e => {
                                    const next = edit.notifyChannels.filter(c => c !== ch);
                                    if (e.target.checked) next.push(ch);
                                    // Never leave it with nowhere to go — that's what "Off" is for.
                                    setE("notifyChannels", next.length ? next : [ch === "email" ? "slack" : "email"]);
                                  }} />
                                <span>{CHANNEL_LABEL[ch]}</span>
                              </label>
                            );
                          })}
                        </div>
                        {agentsData.slackChannelsLoaded && !(agentsData.slackChannels || []).length && (
                          <span className="ag-hint">Connect Slack in Settings to send these there too.</span>
                        )}
                      </label>
                    )}
                    {edit.notifyMode !== "OFF" && edit.notifyChannels.indexOf("slack") !== -1
                      && !!(agentsData.slackChannels || []).length && (
                      <label className="ag-field">
                        <span className="ag-field__label">Slack channel</span>
                        <AgentSelect value={edit.slackChannelId} searchable
                          onChange={v => setE("slackChannelId", v)}
                          options={[{ value: "", label: "Your team's channel" }].concat(
                            (agentsData.slackChannels || []).map(c => ({
                              value: c.id, label: "#" + c.name + (c.teamDefault ? " (team's channel)" : "")
                            })))} />
                        <span className="ag-hint">This agent posts here instead of the team's channel.</span>
                      </label>
                    )}
                  </div>
                  <label className="ag-toggle-field" style={{ marginTop: 4 }}>
                    <input type="checkbox" checked={edit.requireApproval} onChange={e => setE("requireApproval", e.target.checked)} />
                    <span>
                      <b>Require my approval before acting</b>
                      <span className="ag-hint" style={{ marginTop: 2 }}>Every run pauses for your review before the agent acts.</span>
                    </span>
                  </label>
                  <div className="ag-form__actions">
                    <button className="ag-btn ag-btn--primary" disabled={busy || !edit.name.trim()} onClick={saveSettings}><i className="material-icons-outlined">check</i>
                      {busy ? "Saving…" : "Save changes"}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

        </div>
      </div>

      {askRun && (
        <div className="af-picker__overlay" onMouseDown={() => setAskRun(null)}>
          <div className="af-picker" onMouseDown={e => e.stopPropagation()}>
            <div className="af-picker__head">
              <h3>Run agent</h3>
              <button className="ag-seqm__close" onClick={() => setAskRun(null)}>×</button>
            </div>
            <div className="af-picker__ctx">Enter the inputs for this run.</div>
            <div className="af-picker__body">
              {askRun.fields.map(f => (
                <label key={f.key} className="ag-field" style={{ margin: "4px 10px 12px" }}>
                  <span className="ag-field__label">{f.label}</span>
                  {f.type === "select" && f.options ? (
                    <AgentSelect value={askRun.values[f.key] || ""}
                      onChange={v => setAskRun(a => ({ ...a, values: { ...a.values, [f.key]: v } }))}
                      options={(f.options || []).map(o => ({ value: o.value, label: o.label }))} />
                  ) : (
                    <input className="ag-input" value={askRun.values[f.key] || ""} placeholder="Enter a value…"
                      onChange={e => {
                        // Read the value now: React 16 recycles the event before a functional updater runs.
                        const v = e.target.value;
                        setAskRun(a => ({ ...a, values: { ...a.values, [f.key]: v } }));
                      }} />
                  )}
                </label>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", padding: "0 14px 16px" }}>
              <button className="ag-btn" onClick={() => setAskRun(null)}><i className="material-icons-outlined">close</i>Cancel</button>
              <button className="ag-btn ag-btn--primary" disabled={!askRunReady} onClick={submitAskRun}><i className="material-icons-outlined">play_arrow</i>Run</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default AgentDetail;
